import _ from "lodash";
import React from "react";
import classes from "./StudentTemplates.scss";
import PropTypes from "prop-types";
import TemplateAttachment from "UIComponents/TemplateAttachment";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import UseTemplateModal from "UIComponents/UseTemplateModal";
import UIButton from "UIComponents/UIButton";
import I18nHOC from "UIComponents/I18nHOC";
import MediaModal from "UIComponents/MediaModal";
import UIModal from "UIComponents/UIModal";
import { SNPv2_VISITOR_MODULE_PERM } from "Constants/permissionConstants";
import { replaceEditPreview } from "modules/Services";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";
import { Button, IconButton } from "@toddle-edu/ds-web";
import { CloseOutlined, TickCircleOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { showNewTheatreMode } from "Utils/GetTheatreModeType";
import { getSubmissionTemplateUploadStatus } from "ClassRoom/modules/utils";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { createAttachmentGroupsV2 } from "ClassRoom/routes/ClassRoomDetails/routes/StudentAssignment/module/StudentAssignmentModule";

const InstructionsCard = props => {
  const { onClose, onConfirm, t } = props;

  const instructionTextClass = classNames({
    [classes.instructionText]: true,
    ["text-body-1"]: true,
  });
  return (
    <UIModal
      isOpen={true}
      onRequestClose={onClose}
      modalContent={classes.instructionModalContent}
    >
      <div className={classes.instructionContainer}>
        <div className={classes.instructionHeader}>
          <div className="heading-3">{t("common:instruction_plural")}</div>
          <IconButton
            icon={<CloseOutlined />}
            onClick={onClose}
            variant="plain"
          />
        </div>
        <div className={instructionTextClass}>
          <div className="text-body-1">
            {t("common:test_start_instruction_msg")}
          </div>
        </div>
        <div className={classes.footer}>
          <Button size="large" variant="outlined" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            size="large"
            variant="progressive"
            icon={<TickCircleOutlined />}
            onClick={onConfirm}
          >
            {t("common:start")}
          </Button>
        </div>
      </div>
    </UIModal>
  );
};

const StudentTemplates = React.memo(
  ({
    value,
    onUseTemplateClick,
    label,
    canCopy,
    createAttachmentGroupsV2,
    submissionId,
    canTurnIn,
    userType,
    templateContainerStyle,
    myWorkAttachments,
    assignmentCourseId,
    t,
    templateAttachmentWrapperStyle,
    showMediaCard,
    attachmentBottomComponent,
    onClickCreatePost,
    showAddToJournal,
    attachmentResponseObject,
    onMediaClick,
    isSubmitted,
    resetStudentTemplate,
    isScheduled = false,
    isArchivedClass,
    studentAssignmentId,
    isSubmissionLockedForStudent,
    isSubmissionStateLocked,
    isV2 = false,
    isArchivedSubmission = false,
    assignmentState,
    ltiIntegrationCreds,
  }) => {
    // local state
    const [isLoading, setLoading] = React.useState(false);
    const [selectedTemplate, setSelectedTemplate] = React.useState(null);
    const [isTemplateModal, toggleTemplateModal] = React.useState(false);
    const [mediaModalMode, setMediaModalMode] = React.useState("close");
    const [instructionCard, setInstructionCard] = React.useState(null);

    const shouldUseWorkbook = ACLStore.can("FeatureFlag:Workbook");

    const toggleMediaModal = isOpen => {
      if (!isOpen) {
        setMediaModalMode("close");
      }
    };

    const toggleInstructionCard = () => {
      setInstructionCard(!instructionCard);
    };

    //this function will only call from instruction card
    //and instruction card will be only called if in case of un-attempted quiz
    //so here no need of check of shouldShowEditResponse
    const onStartQuiz = () => {
      setInstructionCard(null);

      onUseTemplateClick(instructionCard);
    };

    const _useTemplateClick = async ({
      templateGroup,
      shouldShowEditResponse,
      showPreview,
    }) => {
      const template = _.get(templateGroup, ["attachments", "0"]);

      // don't open workbook for parent
      if (
        typeof onUseTemplateClick === "function" &&
        userType !== "parent" &&
        !showPreview
      ) {
        onUseTemplateClick(templateGroup, shouldShowEditResponse, isSubmitted);
        return;
      }

      // open media modal for parent
      if (userType === "staff" || userType === "parent" || showPreview) {
        setSelectedTemplate([template]);
        const mediaModes = [
          "IMAGE",
          "VIDEO",
          "AUDIO",
          "WORKBOOK",
          "FILE",
          "QUESTION",
          "TODDLE_DOC",
        ];
        const attachmentType = _.get(template, "type", null);
        const isMedia = mediaModes.includes(attachmentType);
        if (showNewTheatreMode()) {
          const submittedTemplate = _.find(myWorkAttachments, item => {
            return (
              _.get(item, "studentTemplate.id", "") ===
              _.get(templateGroup, "id")
            );
          });
          onMediaClick(
            shouldShowEditResponse ? submittedTemplate : templateGroup
          );
          return;
        }
        if (isMedia) {
          if (showPreview || _.includes(["QUESTION"], attachmentType)) {
            setMediaModalMode("view");
          } else {
            setMediaModalMode("edit");
          }
        } else {
          openTemplateModal(getTemplateToOpen(templateGroup));
        }
        return;
      }

      const hasPermissions = !!_.get(template, "metadata.permission", false);
      const isImage = _.get(template, "type", null) === "IMAGE";
      const shouldOpenInWorkbook = shouldUseWorkbook && isImage;
      if ((!canCopy || !hasPermissions) && !shouldOpenInWorkbook) {
        openTemplateModal(getTemplateToOpen(templateGroup));
        return;
      }
      setLoading(true);

      try {
        let returnedTemplate;
        if (
          shouldShowEditResponse ||
          userType === "parent" ||
          userType === "staff"
        ) {
          returnedTemplate = templateGroup;
        } else {
          const result = await makeTemplateCopy(templateGroup.id);

          returnedTemplate = _.get(result, ["0"], {});
        }

        if (!_.isEmpty(returnedTemplate)) {
          const returnedAttachment = _.get(returnedTemplate, [
            "attachments",
            "0",
          ]);
          setSelectedTemplate([returnedAttachment]);
          const isImage = _.get(returnedAttachment, "type", "") === "IMAGE";
          if (isImage) {
            setMediaModalMode("edit");
          } else {
            openTemplateModal(getTemplateToOpen(returnedTemplate));
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const getTemplateToOpen = templateGroup => {
      const template = _.get(templateGroup, ["attachments", "0"]);
      const isLinkType = _.get(template, "type", "") === "LINK";
      if (isLinkType) {
        if (userType === "parent") {
          const { url = "" } = template;
          template.url = replaceEditPreview(url);
        }
        return template;
      } else {
        return [template];
      }
    };

    const openTemplateModal = templateToOpen => {
      setSelectedTemplate(templateToOpen);
      toggleTemplateModal(true);
    };

    const makeTemplateCopy = async templateGroupId => {
      const input = {
        parentId: submissionId,
        parentType: "STUDENT_ASSIGNMENT_SUBMISSION",
        studentTemplateId: templateGroupId,
        copyFromstudentTemplate: canCopy,
      };
      try {
        const result = await createAttachmentGroupsV2({
          input,
        });
        return result;
      } catch (error) {
        console.error(error);
      }
    };
    const isSelectedTemplatePdf =
      _.get(_.first(selectedTemplate), "type") === "FILE" &&
      _.get(_.first(selectedTemplate), "mimeType") === "application/pdf";

    const isEditable =
      (canTurnIn && userType === "student" && mediaModalMode === "edit") ||
      (userType === "staff" &&
        !ACLStore.can(SNPv2_VISITOR_MODULE_PERM) &&
        !isSelectedTemplatePdf); // Template should not be editable for staff with visitor designation or if the template is a pdf

    const showMediaModal =
      mediaModalMode === "edit" || mediaModalMode === "view";

    return (
      <div className={classes.container} style={templateContainerStyle}>
        {showMediaModal && (
          <MediaModal
            toggleMediaModal={toggleMediaModal}
            attachments={selectedTemplate}
            activeIndex={0}
            showWorkBook={isEditable}
            mode={isEditable ? "edit" : "view"}
          />
        )}

        {selectedTemplate && isTemplateModal && (
          <UseTemplateModal
            open={true}
            onClose={() => toggleTemplateModal(false)}
            attachment={selectedTemplate}
            rightActionButton={
              canTurnIn &&
              userType === "student" && (
                <UIButton
                  size="sm"
                  color="pink"
                  type="filled"
                  onClick={() => {
                    toggleTemplateModal(false);
                  }}
                >
                  {t("common:save_exit")}
                </UIButton>
              )
            }
            shouldShowTabIcon={userType !== "parent"}
            studentAssignmentId={studentAssignmentId}
          />
        )}
        {instructionCard && (
          <InstructionsCard
            t={t}
            onClose={toggleInstructionCard}
            onConfirm={onStartQuiz}
          />
        )}
        {isLoading && <FullScreenLoader />}
        {label && <div className={classes.title}>{label}</div>}
        {_.map(value, templateGroup => {
          const templateId = _.get(templateGroup, "id", "");

          const template = _.get(templateGroup, ["attachments", "0"], {});
          const submittedTemplate = _.find(myWorkAttachments, item => {
            return _.get(item, "studentTemplate.id", "") === templateId;
          });

          const shouldShowEditResponse = !_.isEmpty(submittedTemplate);
          const isFeedbackGiven = _.get(
            attachmentResponseObject,
            _.get(submittedTemplate, "id")
          );

          const onUseTemplateClickLocal = (event, showPreview = false) => {
            event.stopPropagation();
            if (isFeedbackGiven) {
              onMediaClick(submittedTemplate, false, true);
            }

            _useTemplateClick({
              templateGroup: shouldShowEditResponse
                ? submittedTemplate
                : templateGroup,
              shouldShowEditResponse,
              showPreview,
            });
          };

          const onMediaClickLocal = showNewTheatreMode()
            ? () =>
                onMediaClick(
                  shouldShowEditResponse ? submittedTemplate : templateGroup
                )
            : undefined;

          const {
            isStudentTemplateUploadFailed,
            isStudentTemplateUploading,
          } = getSubmissionTemplateUploadStatus(templateGroup);

          return isStudentTemplateUploading ? (
            <div className={classes.skeletonLoaderContainer}>
              <SkeletonLoader width="40px" height="40px" />
              <div className={classes.skeletonInnerContainer}>
                <SkeletonLoader width="128px" height="12px" />
                <SkeletonLoader width="64px" height="12px" />
              </div>
            </div>
          ) : !isStudentTemplateUploadFailed ? (
            <TemplateAttachment
              canTurnIn={canTurnIn}
              attachment={template}
              key={templateGroup.id}
              onUseTemplateClick={onUseTemplateClickLocal}
              userType={userType}
              shouldShowEditResponse={shouldShowEditResponse}
              assignmentCourseId={assignmentCourseId}
              wrapperStyle={templateAttachmentWrapperStyle}
              showMediaCard={showMediaCard}
              attachmentBottomComponent={() =>
                _.isFunction(attachmentBottomComponent) &&
                attachmentBottomComponent({ attachment: submittedTemplate })
              }
              onClickCreatePost={() =>
                onClickCreatePost(
                  _.get(submittedTemplate, ["attachments", "0"], {})
                )
              }
              onMediaClick={onMediaClickLocal}
              showAddToJournal={showAddToJournal}
              isFeedbackGiven={isFeedbackGiven}
              submittedTemplate={submittedTemplate}
              resetStudentTemplate={resetStudentTemplate}
              isSubmitted={isSubmitted}
              isScheduled={isScheduled}
              isArchivedClass={isArchivedClass}
              isSubmissionLockedForStudent={isSubmissionLockedForStudent}
              isSubmissionStateLocked={isSubmissionStateLocked}
              isV2={isV2}
              isArchivedSubmission={isArchivedSubmission}
              assignmentState={assignmentState}
              ltiIntegrationCreds={ltiIntegrationCreds}
            />
          ) : null;
        })}
      </div>
    );
  }
);

StudentTemplates.propTypes = {
  submissionId: PropTypes.string,
  canCopy: PropTypes.bool,
};

StudentTemplates.defaultProps = {
  canCopy: false,
  canTurnIn: false,
  templateContainerStyle: {},
};

StudentTemplates.displayName = "StudentTemplates";

const mapActionCreators = {
  createAttachmentGroupsV2,
};

const mapStateToProps = (state, ownProps) => {
  return {
    userType: _.get(state, "login.userInfo.user_type"),
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "classRoom"] })
)(StudentTemplates);
